import {
    Box,
    Button,
    Card,
    CardBody,
    Center,
    Divider,
    Flex,
    FormLabel,
    Heading,
    HStack,
    IconButton,
    PopoverTrigger,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Tfoot,
    Tr,
    useDisclosure,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon, PlusSquareIcon } from '@chakra-ui/icons';
import { FiRefreshCcw } from 'react-icons/fi';
import { useIntl } from 'react-intl';
import { useGetCombinedFeedPagination } from '../../query/resource-hooks/company';
import { CombinedEntry, CombinedFeedFilter } from '../../types';
import { AttendanceTableItem } from '../lists/AttendanceTableItem';
import { CombinedFeedAbsenceItem } from '../absence/CombinedFeedAbsenceItem';
import { AttendanceDetailModal } from './AttendanceDetailModal';
import { AbsenceDetailDrawer } from '../absence/AbsenceDetailDrawer';
import { UpdateAttendanceModal } from './UpdateAttendanceModal';
import { CreateAbsenceDrawer } from '../absence/CreateAbsenceDrawer';
import UserPolicy from '../../policies/UserPolicy';
import { AttendanceTableSkeleton } from '../../screens/attendance/AttendanceTableSkeleton';
import { useAuthStore } from '../../store/auth/authStore';
import { RangeDatepicker } from '../calendarPicker';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { adjustDateToUserTimezone } from '../calendarPicker';
import React, { useState } from 'react';

type CombinedFeedTabProps = {
    filter?: Omit<CombinedFeedFilter, 'cursor' | 'dateFrom' | 'dateTo'>;
};

export const CombinedFeedTab: React.FC<CombinedFeedTabProps> = ({ filter = {} }) => {
    const intl = useIntl();
    const loggedUser = useAuthStore((s) => s.user);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);

    const query = useGetCombinedFeedPagination({
        ...filter,
        ...(selectedDates[0] && { dateFrom: adjustDateToUserTimezone(selectedDates[0]).toISOString().split('T')[0] }),
        ...(selectedDates[1] && { dateTo: adjustDateToUserTimezone(selectedDates[1]).toISOString().split('T')[0] }),
    });

    const attendanceModal = useDisclosure();
    const absenceDrawer = useDisclosure();
    const addAttendanceModal = useDisclosure();
    const createAbsenceDrawer = useDisclosure();
    const [selectedAttendanceId, setSelectedAttendanceId] = React.useState<number>();
    const [selectedAbsenceId, setSelectedAbsenceId] = React.useState<number>();

    const items = query.data?.pages.flatMap((page) => page.data);

    const handleAttendanceClick = (id: number) => {
        setSelectedAttendanceId(id);
        attendanceModal.onOpen();
    };

    const handleAbsenceClick = (id: number) => {
        setSelectedAbsenceId(id);
        absenceDrawer.onOpen();
    };

    const getDateDividerLabel = (entry: CombinedEntry): string => {
        const dividerDateStr = entry.date.substring(0, 10);
        const today = new Date();
        const todayStr = today.toISOString().substring(0, 10);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().substring(0, 10);

        if (dividerDateStr === todayStr) {
            return intl.formatMessage({ id: 'timefilter.today' });
        }
        if (dividerDateStr === yesterdayStr) {
            return intl.formatMessage({ id: 'timefilter.yesterday' });
        }
        return intl.formatDate(entry.date, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    };

    return (
        <Box display="flex" justifyContent="center">
            <Box maxW={1680} width={'100%'}>
                <Stack flexDir={{ base: 'column', lg: 'row' }} width={'100%'}>
                    <Box order={{ base: 2, lg: 1 }} width={'100%'}>
                        <Flex justify={'space-between'} align={'center'} px={2}>
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    ml={2}
                                    leftIcon={<PlusSquareIcon />}
                                    rightIcon={<ChevronDownIcon />}
                                    boxShadow={'md'}
                                >
                                    {intl.formatMessage({ id: 'tab.Add' })}
                                </MenuButton>
                                <MenuList>
                                    <MenuItem
                                        isDisabled={!UserPolicy.canCreateManualAttendance(loggedUser!)}
                                        onClick={addAttendanceModal.onOpen}
                                    >
                                        {intl.formatMessage({ id: 'attendance' })}
                                    </MenuItem>
                                    <MenuItem onClick={createAbsenceDrawer.onOpen}>{intl.formatMessage({ id: 'absence.title' })}</MenuItem>
                                </MenuList>
                            </Menu>
                            <IconButton
                                icon={<FiRefreshCcw />}
                                aria-label="refresh"
                                m={2}
                                variant={'text'}
                                onClick={() => query.refetch()}
                            />
                        </Flex>

                        {query.isLoading ? (
                            <AttendanceTableSkeleton />
                        ) : (
                            <TableContainer>
                                <Table
                                    style={{ borderCollapse: 'separate', borderSpacing: '0 7px' }}
                                    variant={'unstyled'}
                                    size={'sm'}
                                    overflow={'hidden'}
                                >
                                    <Tbody>
                                        {!items || items.length === 0 ? (
                                            <Tr>
                                                <Td colSpan={4}>
                                                    <Center>
                                                        <Text fontWeight={600}>{intl.formatMessage({ id: 'noData' })}</Text>
                                                    </Center>
                                                </Td>
                                            </Tr>
                                        ) : (
                                            items.map((entry, index) => {
                                                if (entry.recordType === 'date_divider') {
                                                    return (
                                                        <Tr key={`divider-${entry.date}-${index}`}>
                                                            <Td colSpan={4} py={2} px={4}>
                                                                <Flex align="center" gap={3}>
                                                                    <Divider />
                                                                    <Text
                                                                        fontSize="xs"
                                                                        fontWeight={600}
                                                                        color="gray.500"
                                                                        whiteSpace="nowrap"
                                                                    >
                                                                        {getDateDividerLabel(entry)}
                                                                    </Text>
                                                                    <Divider />
                                                                </Flex>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                }

                                                if (entry.recordType === 'login' && entry.login) {
                                                    return (
                                                        <AttendanceTableItem
                                                            key={`login-${entry.login.id}`}
                                                            item={entry.login}
                                                            onClick={(data) => {
                                                                if ('item' in data) handleAttendanceClick(data.item.id);
                                                            }}
                                                        />
                                                    );
                                                }

                                                if (entry.recordType === 'absence' && entry.absence) {
                                                    return (
                                                        <CombinedFeedAbsenceItem
                                                            key={`absence-${entry.absence.id}-${index}`}
                                                            item={entry.absence}
                                                            absenceDayIndex={entry.absenceDayIndex}
                                                            absenceTotalDays={entry.absenceTotalDays}
                                                            onClick={() => handleAbsenceClick(entry.absence!.id)}
                                                        />
                                                    );
                                                }

                                                return null;
                                            })
                                        )}
                                    </Tbody>
                                    <Tfoot>
                                        <Tr>
                                            <Td colSpan={4}>
                                                {query.hasNextPage && (
                                                    <Flex p={2} width={'full'} alignItems={'center'} justifyContent={'center'}>
                                                        <Button
                                                            colorScheme="primary"
                                                            onClick={() => query.fetchNextPage()}
                                                            isLoading={query.isFetchingNextPage}
                                                        >
                                                            {intl.formatMessage({ id: 'viewMore' })}
                                                        </Button>
                                                    </Flex>
                                                )}
                                            </Td>
                                        </Tr>
                                    </Tfoot>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                    <Box order={{ base: 1, lg: 2 }}>
                        <Card boxShadow={'md'} position="sticky" top="22px" mt={4} ml={{ base: 2, lg: 0 }} mr={2}>
                            <CardBody>
                                <Heading size={'md'} mb={2}>
                                    {intl.formatMessage({ id: 'filter' })}
                                </Heading>
                                <FormLabel>{intl.formatMessage({ id: 'period' })}</FormLabel>
                                <HStack>
                                    <RangeDatepicker
                                        selectedDates={selectedDates}
                                        onDateChange={setSelectedDates}
                                        usePortal
                                        configs={{
                                            placeHolders: [intl.formatMessage({ id: 'dateFrom' }), intl.formatMessage({ id: 'dateTo' })],
                                        }}
                                    >
                                        {(dates) => (
                                            <PopoverTrigger>
                                                <Box>
                                                    <TimeRangeVisualizer
                                                        interactive
                                                        cursor={'pointer'}
                                                        dateFrom={
                                                            dates[0] ? intl.formatDate(dates[0]) : intl.formatMessage({ id: 'dateFrom' })
                                                        }
                                                        dateTo={dates[1] ? intl.formatDate(dates[1]) : intl.formatMessage({ id: 'dateTo' })}
                                                    />
                                                </Box>
                                            </PopoverTrigger>
                                        )}
                                    </RangeDatepicker>
                                    {selectedDates.length > 0 && (
                                        <IconButton
                                            onClick={() => setSelectedDates([])}
                                            aria-label="clear dates"
                                            size={'sm'}
                                            icon={<CloseIcon color={'red.500'} />}
                                            variant={'ghost'}
                                        />
                                    )}
                                </HStack>
                            </CardBody>
                        </Card>
                    </Box>
                </Stack>

                <AttendanceDetailModal
                    isOpen={attendanceModal.isOpen}
                    onClose={attendanceModal.onClose}
                    currentId={selectedAttendanceId}
                    loggedUser={loggedUser ?? undefined}
                />

                <AbsenceDetailDrawer
                    isOpen={absenceDrawer.isOpen}
                    onClose={absenceDrawer.onClose}
                    absenceId={selectedAbsenceId}
                    onActionComplete={() => query.refetch()}
                />

                <UpdateAttendanceModal
                    isOpen={addAttendanceModal.isOpen}
                    onClose={addAttendanceModal.onClose}
                    onSuccess={() => query.refetch()}
                    preselectedDate={new Date().toISOString()}
                />

                <CreateAbsenceDrawer
                    isOpen={createAbsenceDrawer.isOpen}
                    onClose={createAbsenceDrawer.onClose}
                    onSuccess={() => query.refetch()}
                />
            </Box>
        </Box>
    );
};
